import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import {
  AlertTriangle, Shield, Clock, Play, Bell, Zap, Activity, Server
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

const EmergencyActions = () => {
  const [emergencyProcedures, setEmergencyProcedures] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyForm, setEmergencyForm] = useState({
    type: '',
    severity: 'high',
    description: '',
    affectedSystems: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [proceduresResponse, statusResponse] = await Promise.all([
          MockDataService.getEmergencyProcedures(),
          MockDataService.getSystemStatus()
        ]);

        if (proceduresResponse.success) {
          setEmergencyProcedures(proceduresResponse.data);
        }
        if (statusResponse.success) {
          setSystemStatus(statusResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch emergency data.");
        console.error("Error fetching emergency data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleExecuteProcedure = async (procedureId) => {
    try {
      const response = await MockDataService.executeEmergencyProcedure(procedureId);
      if (response.success) {
        toast.success('Emergency procedure executed successfully');
        setShowExecuteModal(false);
        setSelectedProcedure(null);
      } else {
        toast.error('Failed to execute emergency procedure');
      }
    } catch (err) {
      toast.error('Failed to execute emergency procedure');
      console.error("Error executing procedure:", err);
    }
  };

  const handleInitiateEmergency = async () => {
    try {
      const response = await MockDataService.initiateEmergency(emergencyForm);
      if (response.success) {
        toast.success('Emergency initiated successfully');
        setShowEmergencyModal(false);
        setEmergencyForm({
          type: '',
          severity: 'high',
          description: '',
          affectedSystems: []
        });
      } else {
        toast.error('Failed to initiate emergency');
      }
    } catch (err) {
      toast.error('Failed to initiate emergency');
      console.error("Error initiating emergency:", err);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'success';
      case 'degraded': return 'secondary';
      case 'critical': return 'destructive';
      default: return 'outline';
    }
  };

  if (loading) return <div className="p-6 text-center">Loading Emergency Actions...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-12 px-6 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-white/70"></div>

        <div className="container mx-auto relative z-10">
          <div className="flex justify-between items-center">
            <div className="space-y-3">
              <div className="inline-block px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full">
                <span className="text-sm font-semibold text-red-600">⚠️ Emergency Control Center</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 drop-shadow-lg">
                Emergency Actions
              </h1>
              <p className="text-lg text-gray-600">
                Manage emergency procedures and system status monitoring
              </p>
            </div>
            <Button 
              onClick={() => setShowEmergencyModal(true)}
              className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow bg-red-600 hover:bg-red-700"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Initiate Emergency
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="p-6 relative">

      {/* System Status Overview */}
      {systemStatus && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
              <CardTitle className="text-sm font-medium text-muted-foreground">Overall Status</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
                <Shield className="h-4 w-4 text-white" />
              </div>
            </CardHeader>
            <CardContent className="relative z-10">
              <div className="text-3xl font-bold text-foreground mb-2">{systemStatus.overallStatus}</div>
              <Badge variant={getStatusColor(systemStatus.overallStatus)} className="mt-2">
                {systemStatus.overallStatus}
              </Badge>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
              <CardTitle className="text-sm font-medium text-muted-foreground">System Uptime</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500">
                <Clock className="h-4 w-4 text-white" />
              </div>
            </CardHeader>
            <CardContent className="relative z-10">
              <div className="text-3xl font-bold text-foreground mb-2">{systemStatus.uptime}</div>
              <p className="text-xs text-muted-foreground">Last restart: {systemStatus.lastRestart}</p>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Alerts</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-pink-500">
                <Bell className="h-4 w-4 text-white" />
              </div>
            </CardHeader>
            <CardContent className="relative z-10">
              <div className="text-3xl font-bold text-foreground mb-2">{systemStatus.activeAlerts}</div>
              <p className="text-xs text-muted-foreground">{systemStatus.criticalAlerts} critical</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Emergency Procedures */}
      <Card className="mb-8 group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <CardHeader className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 shadow-glow">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle>Emergency Procedures</CardTitle>
              <CardDescription>Critical system emergency response procedures</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          <div className="space-y-4">
            {emergencyProcedures.length > 0 ? emergencyProcedures.map((procedure) => (
              <div key={procedure.id} className="border-2 rounded-xl p-4 hover:border-primary/50 hover:shadow-elegant transition-all bg-card/50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{procedure.name}</h3>
                    <p className="text-gray-600 mt-1">{procedure.description}</p>
                    <div className="flex items-center space-x-4 mt-3">
                      <Badge variant={getSeverityColor(procedure.priority)}>
                        {procedure.priority}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        ⏱️ Duration: {procedure.estimatedDuration}
                      </span>
                      <span className="text-sm text-gray-600">
                        👥 Personnel: {procedure.requiredPersonnel}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="default"
                      size="sm"
                      className="gradient-primary text-primary-foreground hover:opacity-90"
                      onClick={() => {
                        setSelectedProcedure(procedure);
                        setShowExecuteModal(true);
                      }}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Execute
                    </Button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-600">
                No emergency procedures available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Components Status */}
      {systemStatus && systemStatus.components && (
        <Card className="mb-8 group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="relative z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-violet-500 shadow-glow">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle>System Components Status</CardTitle>
                <CardDescription>Real-time status of all system components</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {systemStatus.components.map((component) => (
                <div key={component.name} className="border-2 rounded-xl p-4 hover:border-primary/50 hover:shadow-elegant transition-all bg-card/50">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <Server className="h-4 w-4 text-gray-500" />
                      <h4 className="font-bold text-gray-900">{component.name}</h4>
                    </div>
                    <Badge variant={getStatusColor(component.status)}>
                      {component.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{component.description}</p>
                  <div className="mt-3 pt-3 border-t flex justify-between text-xs">
                    <span className="text-gray-600">CPU: <strong>{component.cpu}%</strong></span>
                    <span className="text-gray-600">Memory: <strong>{component.memory}%</strong></span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>

      {/* Execute Procedure Modal */}
      <Modal 
        isOpen={showExecuteModal} 
        onClose={() => setShowExecuteModal(false)}
        title="Execute Emergency Procedure"
        size="lg"
      >
          {selectedProcedure && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedProcedure.name}</h3>
                <p className="text-muted-foreground mt-1">{selectedProcedure.description}</p>
              </div>
              {selectedProcedure.steps && selectedProcedure.steps.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Procedure Steps:</h4>
                  <ol className="list-decimal list-inside space-y-2">
                    {selectedProcedure.steps.map((step, index) => (
                      <li key={index} className="text-sm">
                        <strong>{step.title || `Step ${index + 1}`}:</strong> {step.description}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowExecuteModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleExecuteProcedure(selectedProcedure.id)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Execute Procedure
                </Button>
              </div>
            </div>
          )}
      </Modal>

      {/* Initiate Emergency Modal */}
      <Modal 
        isOpen={showEmergencyModal} 
        onClose={() => setShowEmergencyModal(false)}
        title="Initiate Emergency"
        size="md"
      >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Emergency Type</label>
              <select
                value={emergencyForm.type}
                onChange={(e) => setEmergencyForm({...emergencyForm, type: e.target.value})}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Select emergency type</option>
                <option value="system_failure">System Failure</option>
                <option value="security_breach">Security Breach</option>
                <option value="data_corruption">Data Corruption</option>
                <option value="network_outage">Network Outage</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Severity</label>
              <select
                value={emergencyForm.severity}
                onChange={(e) => setEmergencyForm({...emergencyForm, severity: e.target.value})}
                className="w-full p-2 border rounded-md"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={emergencyForm.description}
                onChange={(e) => setEmergencyForm({...emergencyForm, description: e.target.value})}
                className="w-full p-2 border rounded-md"
                rows={3}
                placeholder="Describe the emergency situation..."
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEmergencyModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleInitiateEmergency}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Initiate Emergency
              </Button>
            </div>
          </div>
      </Modal>
    </div>
  );
};

export default EmergencyActions;